import React, { useState } from 'react';
import {
    Box, List, ListItem, ListItemIcon, ListItemText, Divider, Typography, Paper,
    Button, Drawer, AppBar, Toolbar, IconButton, useTheme, useMediaQuery, ListItemButton
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../../features/auth';

const drawerWidth = 260;

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
        { text: 'Gói Dịch Vụ', icon: <InventoryIcon />, path: '/admin/packages' },
        { text: 'Mã Giảm Giá', icon: <LocalOfferIcon />, path: '/admin/promotions' },
        { text: 'Người Dùng', icon: <PeopleIcon />, path: '/admin/users' },
        { text: 'Đơn Hàng', icon: <ReceiptIcon />, path: '/admin/orders' },
        { text: 'Lịch Sử Giao Dịch', icon: <AccountBalanceWalletIcon />, path: '/admin/transactions' },
        { text: 'Gói Đăng Ký', icon: <CardMembershipIcon />, path: '/admin/subscriptions' },
    ];

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a', color: '#e2e8f0' }}>
            <Box p={3} textAlign="center" borderBottom="1px solid rgba(255,255,255,0.1)">
                <Typography variant="h6" fontWeight="bold" sx={{ background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)', backgroundClip: 'text', textFillColor: 'transparent', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    ADMIN PANEL
                </Typography>
            </Box>

            <List sx={{ p: 2, flexGrow: 1 }}>
                {menuItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                            onClick={() => {
                                navigate(item.path);
                                if (isMobile) setMobileOpen(false);
                            }}
                            selected={location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path))}
                            sx={{
                                borderRadius: 2,
                                color: '#94a3b8',
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                                    color: '#60a5fa',
                                    '& .MuiListItemIcon-root': { color: '#60a5fa' }
                                },
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#f1f5f9' },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

            <Box p={2}>
                <Button
                    fullWidth
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/home')}
                    sx={{
                        justifyContent: 'flex-start',
                        color: '#94a3b8',
                        mb: 1,
                        '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.05)' }
                    }}
                >
                    Về Trang Chủ
                </Button>
                <Button
                    fullWidth
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{ justifyContent: 'flex-start' }}
                >
                    Đăng Xuất
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#020617">
            {/* Mobile Header */}
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    display: { md: 'none' },
                    bgcolor: '#0f172a',
                    backgroundImage: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        Admin Dashboard
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Sidebar Drawer */}
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                {/* Mobile */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawerContent}
                </Drawer>
                {/* Desktop */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid rgba(255,255,255,0.1)' },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    pt: { xs: 8, md: 3 }, // Padding for mobile header
                    color: '#e2e8f0',
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    overflowX: 'auto'
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

export default AdminLayout;
